import { gql, useQuery } from '@apollo/client';
import { Title } from 'components/common/BrowserMetaData';
import { UserContext } from 'contexts/user/UserContextProvider';
import { getTranslatedString } from 'helpers/utils-typescript';
import { SPECIMEN_FRAGMENT } from 'pages/Bioregion/CreateEditSpecimenForm/SpecimenIdentification';
import SpecimenDetailsDesktopLayout from 'pages/Bioregion/Specimen/SpecimenDetailsDesktopLayout';
import SpecimenDetailsMobileLayout from 'pages/Bioregion/Specimen/SpecimenDetailsMobileLayout';
import React, { Fragment, ReactElement, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import SpecimenContextHelpModal from './SpecimenContextHelpModal';

export const SPECIMEN_BY_SLUG = gql`
${SPECIMEN_FRAGMENT}
query specimenBySlug($slug: String!) {
  specimanBySlug(slug: $slug) {
    ...specimanFragment
    territoriesByCoordinatesCurrentUserMentor: territoriesByCoordinatesGeometry(filter: {isCurrentUserMentor: {equalTo: true}}) {
      totalCount
    }
    speciesSpecimenReviewCommentsBySpecimenId(orderBy: CREATED_AT_DESC) {
      nodes {
        id
        createdAt
        content
        createdByUser {
          avatarUrl
          name
          username
          isTerritoryMentor
        }
      }
    }
    speciesSpecimenCommentsBySpecimenId(orderBy: CREATED_AT_DESC) {
      nodes {
        id
        createdAt
        content
        createdByUser {
          avatarUrl
          name
          username
          isTerritoryMentor
        }
      }
    }
  }
}
`;

/** This component renders the Specimen Details page */
const SpecimenDetailsPage = (): React.ReactElement => {
  const currentLang = useSelector((state: any) => state.settings.locale);
  const history = useHistory();
  const { slug } = useParams() as any;
  const { currentUser } = useContext(UserContext);

  /* */
  /* Query */
  /* */
  const { loading: specimanLoading, error: specimanLoadingError, data } = useQuery(SPECIMEN_BY_SLUG, {
    variables: {
      slug: slug
    }
  });
  const specimanBySlug = data?.specimanBySlug;

  /* */
  /* States */
  /* */
  const [showContextHelpModal, setShowContextHelpModal] = useState(false);
  const [contextHelpId, setContextHelpId] = useState(null);
  const [specimenData, setSpecimenData] = useState<{
    dataWtUnits: string,
    icon: ReactElement<{ height: string, className: string }>
    titleTranslationId: string,
  }>();

  // for approved specimens: can edit if: user is the author, or the commons mentor
  // for not-yet-approved specimens: can edit if user is the author or the reviewer
  const hasEditPermission = useMemo(() => {
    if (specimanBySlug?.status === 'APPROVED_PUBLISHED') {
      return (
        specimanBySlug?.createdByUserId === currentUser?.id ||
        specimanBySlug.territory?.isCurrentUserMentor ||
        specimanBySlug.territoriesByCoordinatesCurrentUserMentor.totalCount > 0 // number of territories in which user is mentor (see query above)
      );
    } else {
      return [specimanBySlug?.createdByUserId, specimanBySlug?.reviewerId].includes(currentUser?.id);
    }
  }, [currentUser, specimanBySlug]);

  /* */
  /* Render */
  /* */
  if (specimanLoading) {
    return <div className="loading" />;
  }
  if (specimanLoadingError) {
    return <h2>{`ARK had a problem loading data: ${specimanLoadingError.message}`}</h2>;
  }
  if (!specimanBySlug) {
    history.push('/error'); // not found
    return (<></>);
  }

  // if the specimen is under review, always use the desktop layout
  // (which actually also contains the old mobile layout, that makes it possible to review the specimen on mobile)
  if (['TO_REVIEW', 'UNDER_REVIEW', 'NEEDS_IMPROVEMENT'].includes(specimanBySlug.status)) {
    return (
      <Fragment>
        <Title sectionTitle={getTranslatedString(specimanBySlug.name, currentLang)} />

        <SpecimenDetailsDesktopLayout
          specimanBySlug={specimanBySlug}
          hasEditPermission={hasEditPermission}
        />

        <SpecimenContextHelpModal
          contextHelpId={contextHelpId}
          showContextHelpModal={showContextHelpModal}
          setShowContextHelpModal={setShowContextHelpModal}
          currentLang={currentLang}
          specimenData={specimenData}
        />
      </Fragment >
    );
  }

  return (
    <Fragment>
      <Title sectionTitle={getTranslatedString(specimanBySlug.name, currentLang)} />

      <div className='d-md-none'>
        <SpecimenDetailsMobileLayout
          hasEditPermission={hasEditPermission}
          specimanBySlug={specimanBySlug}
          setContextHelpId={setContextHelpId}
          setShowContextHelpModal={setShowContextHelpModal}
          setSpecimenData={setSpecimenData}
        />
      </div>

      <div className='d-none d-md-block'>
        <SpecimenDetailsDesktopLayout
          specimanBySlug={specimanBySlug}
          hasEditPermission={hasEditPermission}
        />
      </div>

      <SpecimenContextHelpModal
        contextHelpId={contextHelpId}
        showContextHelpModal={showContextHelpModal}
        setShowContextHelpModal={setShowContextHelpModal}
        currentLang={currentLang}
        specimenData={specimenData}
      />
    </Fragment >
  );
};

export default SpecimenDetailsPage;
